import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Paper, Stack, Typography } from "@mui/material";
import ReceiptLongRounded from "@mui/icons-material/ReceiptLongRounded";
import FavoriteBorderRounded from "@mui/icons-material/FavoriteBorderRounded";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import PendingActionsRoundedIcon from "@mui/icons-material/PendingActionsRounded";
import HomeRepairServiceRoundedIcon from "@mui/icons-material/HomeRepairServiceRounded";

import InquiryAPI from "../../../../api/Inquiry";
import { FavouriteAPI } from "../../../../api/Favourite";
import RetailAPI from "../../../../api/Retail";
import MemberCard from "../MemberCard";
import { fonts, getDisplayName, tForLang } from "../AccountUtils";
import { getLangFromCookie } from "../../../../Utils/LangCookie";
import { resolveCanonicalUserID } from "../../../../Utils/UserIdentity";

function QuickActionCard({
  href,
  icon,
  label,
  count = 0,
  lang,
  hideCount = false,
}) {
  return (
    <Box
      component="a"
      href={href}
      sx={{
        position: "relative",

        width: "100%",
        minWidth: 0,
        minHeight: {
          xs: 138,
          sm: 150,
          md: 160,
        },

        px: {
          xs: 1.5,
          sm: 2,
        },

        py: {
          xs: 2,
          sm: 2.25,
        },

        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,

        bgcolor: "background.paper",

        color: "text.primary",
        textDecoration: "none",

        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",

        textAlign: "center",

        boxSizing: "border-box",

        transition:
          "transform 220ms ease, box-shadow 220ms ease, border-color 220ms ease",

        "&:hover": {
          transform: "translateY(-4px)",

          borderColor: "primary.main",

          boxShadow:
            "0 12px 28px rgba(0, 95, 145, 0.14)",
        },

        "&:focus-visible": {
          outline: "3px solid",
          outlineColor: "primary.main",
          outlineOffset: 3,
        },
      }}
    >
      {!hideCount && (
        <Box
          sx={{
            position: "absolute",

            top: {
              xs: 10,
              sm: 12,
            },

            right: {
              xs: 10,
              sm: 12,
            },

            minWidth: 24,
            height: 24,

            px: 0.75,

            borderRadius: "999px",

            display: "flex",
            alignItems: "center",
            justifyContent: "center",

            bgcolor: "#ff4d4f",
            color: "#ffffff",

            fontSize: 12,
            fontWeight: 700,
            lineHeight: 1,

            boxShadow:
              "0 4px 10px rgba(255, 77, 79, 0.24)",
          }}
        >
          {Number(count || 0)}
        </Box>
      )}

      <Box
        aria-hidden="true"
        sx={{
          width: {
            xs: 54,
            sm: 62,
            md: 68,
          },

          height: {
            xs: 54,
            sm: 62,
            md: 68,
          },

          mb: {
            xs: 1.5,
            sm: 1.75,
          },

          borderRadius: "50%",

          display: "flex",
          alignItems: "center",
          justifyContent: "center",

          color: "#005F91",
          bgcolor: "rgba(0, 95, 145, 0.08)",

          "& .MuiSvgIcon-root": {
            fontSize: {
              xs: 30,
              sm: 34,
              md: 38,
            },
          },
        }}
      >
        {icon}
      </Box>

      <Typography
        sx={{
          width: "100%",

          fontFamily:
            fonts[lang] ||
            fonts.EN,

          fontWeight: 700,

          fontSize: {
            xs: 14,
            sm: 15,
            md: 16,
          },

          lineHeight: 1.35,

          color: "text.primary",

          textAlign: "center",

          whiteSpace: "normal",
          overflowWrap: "anywhere",
          wordBreak: "normal",

          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}

const DETAIL_LABELS = {
  memberId: {
    ZH: "會員編號",
    EN: "Member ID",
    JP: "会員番号",
  },
  fullName: {
    ZH: "姓名",
    EN: "Full Name",
    JP: "氏名",
  },
  phone: {
    ZH: "手機號碼",
    EN: "Phone Number",
    JP: "電話番号",
  },
  email: {
    ZH: "電子郵件",
    EN: "Email",
    JP: "メールアドレス",
  },
  memberDefault: {
    ZH: "SEHO 會員",
    EN: "SEHO Member",
    JP: "SEHO会員",
  },
};

function pickLabel(lang, key) {
  const safeLang = lang === "JP" ? "JP" : lang === "ZH" ? "ZH" : "EN";
  return (
    DETAIL_LABELS?.[key]?.[safeLang] ||
    DETAIL_LABELS?.[key]?.EN ||
    DETAIL_LABELS?.[key]?.ZH ||
    ""
  );
}

function normalizeLang(code) {
  const v = String(code || "")
    .trim()
    .toUpperCase();
  return v === "JP" ? "JP" : v === "ZH" ? "ZH" : "EN";
}

function pickCount(value) {
  if (typeof value === "number") return value;
  if (typeof value === "string" && value.trim() !== "")
    return Number(value) || 0;
  if (value && typeof value === "object") {
    if (typeof value.count !== "undefined") return Number(value.count) || 0;
    if (typeof value.data === "number") return Number(value.data) || 0;
    if (typeof value.data?.count !== "undefined") {
      return Number(value.data.count) || 0;
    }
  }
  return 0;
}

export default function DashboardTab({ user, lang: langProp }) {
  const [cookieLang, setCookieLang] = useState(() =>
    normalizeLang(getLangFromCookie() || "EN"),
  );

  const [counts, setCounts] = useState({
    pending: 0,
    favourites: 0,
    historyNonPending: 0,
    retailHistory: 0,
  });

  useEffect(() => {
    const handleLangChange = () => {
      setCookieLang(normalizeLang(getLangFromCookie() || "EN"));
    };

    window.addEventListener("app:languageChanged", handleLangChange);
    return () =>
      window.removeEventListener("app:languageChanged", handleLangChange);
  }, []);

  const lang = useMemo(
    () => normalizeLang(langProp || cookieLang || "EN"),
    [langProp, cookieLang],
  );

  const t = useCallback((key) => tForLang(lang, key), [lang]);
  const uid = useMemo(() => resolveCanonicalUserID(user), [user]);
  // console.log("Dashboard favourite userID:", uid);

  const loadFavouriteCount = useCallback(async () => {
    if (!uid) {
      setCounts((prev) => ({ ...prev, favourites: 0 }));
      return;
    }

    try {
      const res = await FavouriteAPI.count(String(uid), { lang });
      const favourites = pickCount(res);
      setCounts((prev) => ({ ...prev, favourites }));
    } catch (e) {
      console.error("Failed to load favourite count:", e);
      setCounts((prev) => ({ ...prev, favourites: 0 }));
    }
  }, [uid, lang]);

  const loadPendingCount = useCallback(async () => {
    if (!uid) {
      setCounts((prev) => ({ ...prev, pending: 0 }));
      return;
    }

    try {
      const res = await InquiryAPI.getPendingCount(String(uid), { lang });
      const pending = pickCount(res);
      setCounts((prev) => ({ ...prev, pending }));
    } catch (e) {
      console.error("Failed to load pending inquiry count:", e);
      setCounts((prev) => ({ ...prev, pending: 0 }));
    }
  }, [uid, lang]);

    const loadHistoryNonPendingCount = useCallback(async () => {
    if (!uid) {
      setCounts((prev) => ({ ...prev, historyNonPending: 0 }));
      return;
    }

    try {
      const res = await InquiryAPI.getHistoryNonPendingCount(String(uid), {
        lang,
      });
      const historyNonPending = pickCount(res);
      setCounts((prev) => ({ ...prev, historyNonPending }));
    } catch (e) {
      console.error("Failed to load history non pending inquiry count:", e);
      setCounts((prev) => ({ ...prev, historyNonPending: 0 }));
    }
  }, [uid, lang]);

  const loadRetailHistoryCount = useCallback(async () => {
    if (!uid) {
      setCounts((prev) => ({
        ...prev,
        retailHistory: 0,
      }));

      return;
    }

    try {
      const historyItems =
        await RetailAPI.getHistory(
          String(uid),
        );

      const retailHistory =
        Array.isArray(historyItems)
          ? historyItems.length
          : 0;

      setCounts((prev) => ({
        ...prev,
        retailHistory,
      }));
    } catch (e) {
      console.error(
        "Failed to load repair history count:",
        e,
      );

      setCounts((prev) => ({
        ...prev,
        retailHistory: 0,
      }));
    }
  }, [uid]);

  const loadDashboardCounts = useCallback(async () => {
    if (!uid) {
      setCounts({
        pending: 0,
        favourites: 0,
        historyNonPending: 0,
        retailHistory: 0,
      });

      return;
    }

    await Promise.all([
      loadFavouriteCount(),
      loadPendingCount(),
      loadHistoryNonPendingCount(),
      loadRetailHistoryCount(),
    ]);
  }, [
    uid,
    loadFavouriteCount,
    loadPendingCount,
    loadHistoryNonPendingCount,
    loadRetailHistoryCount,
  ]);

  useEffect(() => {
    loadDashboardCounts();
  }, [loadDashboardCounts]);

  useEffect(() => {
    const reloadFavourite = () => loadFavouriteCount();
    const reloadInquiry = () => {
      loadPendingCount();
      loadHistoryNonPendingCount();
    };

    window.addEventListener("favourite:updated", reloadFavourite);
    window.addEventListener("inquiry:updated", reloadInquiry);
    window.addEventListener("consulting:updated", reloadInquiry);

    return () => {
      window.removeEventListener("favourite:updated", reloadFavourite);
      window.removeEventListener("inquiry:updated", reloadInquiry);
      window.removeEventListener("consulting:updated", reloadInquiry);
    };
  }, [loadFavouriteCount, loadPendingCount, loadHistoryNonPendingCount]);

  if (!user) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
          textAlign: "center",
        }}
      >
        <Typography
          sx={{ fontFamily: fonts[lang] || fonts.EN, fontWeight: 700 }}
        >
          {t("loginPrompt")}
        </Typography>
      </Paper>
    );
  }

  const displayName = getDisplayName(user);
  const pending = counts.pending ?? 0;
  const favourites = counts.favourites ?? 0;
  const historyNonPending = counts.historyNonPending ?? 0;
  const retailHistory = counts.retailHistory ?? 0;

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, md: 3 },
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <Stack
        direction={{ xs: "column", sm: "row", md: "row" }}
        alignItems={{ xs: "flex-start", sm: "center", md: "center" }}
        justifyContent="space-between"
        spacing={{ xs: 2, sm: 2.5, md: 2.5 }}
        sx={{ mb: { xs: 2, sm: 2.5, md: 2.5 } }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Typography
              variant="h6"
              sx={{ fontFamily: fonts[lang] || fonts.EN, fontWeight: 900 }}
            >
              {displayName || pickLabel(lang, "memberDefault")}
            </Typography>
          </Stack>

          <Typography
            sx={{
              mt: 1.25,
              fontFamily: fonts[lang] || fonts.EN,
              color: "text.secondary",
              fontSize: 13,
            }}
          >
            {pickLabel(lang, "memberId")} : {uid || "-"}
          </Typography>

          <Stack spacing={0.25} sx={{ mt: 0.75 }}>
            <Typography
              sx={{
                fontFamily: fonts[lang] || fonts.EN,
                color: "text.secondary",
                fontSize: 13,
              }}
            >
              {pickLabel(lang, "fullName")} : {displayName || "-"}
            </Typography>

            <Typography
              sx={{
                fontFamily: fonts[lang] || fonts.EN,
                color: "text.secondary",
                fontSize: 13,
              }}
            >
              {pickLabel(lang, "phone")} : {user?.phoneNumber || "-"}
            </Typography>

            <Typography
              sx={{
                fontFamily: fonts[lang] || fonts.EN,
                color: "text.secondary",
                fontSize: 13,
              }}
            >
              {pickLabel(lang, "email")} : {user?.email || "-"}
            </Typography>
          </Stack>
        </Box>

        <Box
          sx={{
            width: { xs: "100%", sm: 340 },
            maxWidth: 450,
            height: "auto",
            borderRadius: 2,
            overflow: "hidden",
            flexShrink: 0,
            alignSelf: { xs: "center", md: "flex-end" },
          }}
        >
          <MemberCard user={user} />
        </Box>
      </Stack>

      <Box
        sx={{
          borderTop: "1px solid",
          borderColor: "divider",
          pt: { xs: 1.5, md: 2 },
          pb: { xs: 1, md: 1.5 },
        }}
      >
        <Box
          sx={{
            display: "grid",

            gap: {
              xs: 1.5,
              sm: 2,
              md: 2.25,
            },

            gridTemplateColumns: {
              xs: "repeat(2, minmax(0, 1fr))",
              sm: "repeat(3, minmax(0, 1fr))",
              md: "repeat(5, minmax(0, 1fr))",
            },
          }}
        >
          <QuickActionCard
            href="?tab=basic"
            icon={
              <ReceiptLongRounded />
            }
            label={t(
              "quickBasicInfo",
            )}
            hideCount
            lang={lang}
          />

          <QuickActionCard
            href="/我的收藏"
            icon={
              <FavoriteBorderRounded />
            }
            label={t(
              "quickFavorites",
            )}
            count={favourites}
            lang={lang}
          />

          <QuickActionCard
            href="/詢價/歷史"
            icon={
              <HistoryRoundedIcon />
            }
            label={t(
              "quickInquiryHistory",
            )}
            count={
              historyNonPending
            }
            lang={lang}
          />

          <QuickActionCard
            href="/詢價"
            icon={
              <PendingActionsRoundedIcon />
            }
            label={t(
              "statusPending",
            )}
            count={pending}
            lang={lang}
          />

          <QuickActionCard
            href="/線上報修/紀錄"
            icon={
              <HomeRepairServiceRoundedIcon />
            }
            label={t(
              "quickRetailHistory",
            )}
            count={
              retailHistory
            }
            lang={lang}
          />
        </Box>
      </Box>
    </Paper>
  );
}
